'use no memo';

import type { UserPrivate } from '@repo/types/users';
import type { ColumnDef } from '@tanstack/react-table';

import { useGetParents } from '@repo/api/parent/useGetParents';
import { useGetManagedUsers } from '@repo/api/user/useGetManagedUsers';
import { AccountType } from '@repo/types/enums/accountType';
import { UserLvl } from '@repo/types/enums/userLvl';
import { EditIcon, PlusIcon, Trash2Icon, UserIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import CreateUser from '@/components/features/manage/manage-users/CreateUser';
import DeleteUserDialog from '@/components/features/manage/manage-users/DeleteUser';
import EditUser from '@/components/features/manage/manage-users/EditUser';
import TanstackTable from '@/components/features/manage/tanstack-table/TanstackTable';
import { useTanstackTable } from '@/components/features/manage/tanstack-table/useTanstackTable';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { getAvatar } from '@/utils/getAvatar';

export default function ManageParentsPage() {
  const [activeParent, setActiveParent] = useState<UserPrivate | undefined>();
  const [editOpen, setEditOpen] = useState<boolean>(false);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);

  const { data: parents = [], isPending } = useGetParents();
  const { data: users = [] } = useGetManagedUsers();

  const students = useMemo(() => users.filter((user) => user.lvl === UserLvl.STUDENT && (user.accountType === undefined || user.accountType === AccountType.User)), [users]);

  const columns = useMemo<ColumnDef<UserPrivate>[]>(
    () => [
      {
        meta: 'Awatar',
        id: 'avatarURL',
        accessorKey: 'avatarURL',
        enableSorting: false,
        header: 'Awatar',
        cell: ({ getValue }) => {
          const avatarURL = getValue();
          const url = typeof avatarURL === 'string' ? getAvatar(avatarURL) : undefined;
          return (
            <Avatar size={'sm'}>
              <AvatarImage src={url} />
              <AvatarFallback>
                <UserIcon className={'size-3.5'} />
              </AvatarFallback>
            </Avatar>
          );
        },
      },
      { meta: 'Imię', id: 'name', accessorKey: 'name', header: 'Imię' },
      {
        meta: 'Nazwisko',
        id: 'surname',
        accessorKey: 'surname',
        header: 'Nazwisko',
      },
      {
        meta: 'Email',
        id: 'email',
        accessorKey: 'email',
        header: 'Email',
        cell: ({ getValue }) => {
          const email = getValue() as UserPrivate['email'];
          if (!email) return <p>-</p>;
          return <a href={`mailto:${email}`}>{email}</a>;
        },
      },
      {
        id: 'actions',
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className={'flex w-full justify-end'}>
            <Button
              size={'sm'}
              variant={'ghost'}
              variantColor={'destructive'}
              onClick={() => {
                setActiveParent(row.original);
                setDeleteOpen(true);
              }}
              aria-label={'Usuń rodzica'}
            >
              <Trash2Icon />
            </Button>
            <Button
              size={'sm'}
              variant={'ghost'}
              onClick={() => {
                setActiveParent(row.original);
                setEditOpen(true);
              }}
              aria-label={'Edytuj rodzica'}
            >
              <EditIcon />
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  const { table, setGlobalFilter } = useTanstackTable({
    name: 'parents',
    columns,
    data: parents,
    getRowId: (row) => String(row.id),
  });

  const addGroup = useMemo(
    () => [
      {
        id: 1,
        label: 'Dodaj rodzica',
        icon: <PlusIcon className={'size-4'} />,
        func: () => setCreateOpen(true),
      },
    ],
    []
  );

  return (
    <>
      <CreateUser open={createOpen} onOpenChange={setCreateOpen} accountType={AccountType.Parent} students={students} />
      <EditUser
        user={activeParent}
        open={editOpen}
        onOpenChange={(open) => {
          setEditOpen(open);
          if (!open) setActiveParent(undefined);
        }}
        accountType={AccountType.Parent}
        students={students}
        parents={parents}
      />
      <DeleteUserDialog
        users={activeParent ? [activeParent] : []}
        open={deleteOpen}
        onOpenChange={(open) => {
          setDeleteOpen(open);
          if (!open) setActiveParent(undefined);
        }}
      />
      <div className={'base-direction-col h-full'}>
        <div className={'viewport-height flex w-full flex-col gap-4'}>
          <TanstackTable table={table} label={'Rodzice'} isLoading={isPending} onGlobalFilterChange={setGlobalFilter} addGroup={addGroup} />
        </div>
      </div>
    </>
  );
}
